import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ArrowLeftIcon,
    ChatBubbleLeftRightIcon,
    CheckCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { fetchPatientCard } from '../../../../services/patientCard';
import { resolveInternalImageUrls } from '../../../../services/internalImages';
import { PatientInfoSection } from './editor/PatientInfoSection';
import { FeesSection } from './editor/FeesSection';
import { StepHeader, type StepDef } from './editor/StepHeader';
import { DxReportPreview } from './editor/DxReportPreview';
import { PlainTextStep } from './editor/PlainTextStep';
import { SubStepSplitView, type SubStepDef } from './editor/SubStepSplitView';
import { OrthopulseStatusBar } from './editor/OrthopulseStatusBar';
import { FeedbackStep } from './editor/FeedbackStep';
import { useDxDraftLifecycle } from './editor/useDxDraftLifecycle';
import {
    collectImageFileIds,
    computeAge,
    extractPayloadFromEvent,
    rewriteImageSrcs,
    toDateOnly
} from './editor/payloadHelpers';
import { SPECIAL_CONSENT_PRESETS, ORTHOPULSE_STATUS_PRESET_DOCS } from './templates';
import {
    createInitialPayload,
    isPayloadSubmittable,
    ORTHOPULSE_PRODUCT_NAME,
    setOrthopulseStatus,
    setSectionTiptap,
    tiptapDocFromString,
    validatePayload
} from './editor/editorState';
import {
    fetchPracticeProductPricing,
    type PricedProduct
} from '../../../../services/practiceProductPricing';
import { useToast } from '../../../../providers/ToastProvider';
import type { TimelineEvent } from '../types';
import type {
    DxReportPayload,
    DxReportPatientInfo,
    DxReportFees,
    DxReportSectionId,
    DxReviewHistoryEntry,
    TiptapDoc
} from './types';

interface DxReportEditorProps {
    patientId: string;
    patientName: string;
    onBack: () => void;
    editingEvent?: TimelineEvent;
    resumingDraft?: TimelineEvent;
    startBlank?: boolean;
    onRegisterBeforeClose?: (handler: (() => Promise<void>) | null) => void;
}

type StepId = 'feedback' | 'info' | 'clinical' | 'recommendation' | 'fees' | 'review';

const BASE_STEPS: readonly StepDef<StepId>[] = [
    { id: 'info', label: 'Info', icon: UserIcon },
    { id: 'clinical', label: 'Clinical', icon: ClipboardDocumentListIcon },
    { id: 'recommendation', label: 'Recommendation', icon: DocumentTextIcon },
    { id: 'fees', label: 'Fees', icon: CurrencyDollarIcon },
    { id: 'review', label: 'Review', icon: CheckCircleIcon }
] as const;

const FEEDBACK_STEP: StepDef<StepId> = {
    id: 'feedback',
    label: 'Feedback',
    icon: ChatBubbleLeftRightIcon
};

const INFO_GOAL_SECTIONS: DxReportSectionId[] = ['treatment-goals'];

const STEP_SECTIONS: Record<
    Exclude<StepId, 'feedback' | 'info' | 'fees' | 'review'>,
    DxReportSectionId[]
> = {
    clinical: ['findings', 'comments', 'risks'],
    recommendation: ['special-consent', 'estimated-treatment-time', 'orthopulse-recommendation']
};

type ClinicalSubId = 'findings' | 'comments' | 'risks';
type RecommendationSubId = 'consent' | 'orthopulse' | 'tx-time';

const CLINICAL_SUBS: readonly SubStepDef<ClinicalSubId>[] = [
    { id: 'findings', label: 'Clinical findings', sectionId: 'findings' },
    { id: 'comments', label: 'Comments', sectionId: 'comments' },
    { id: 'risks', label: 'Risks & concerns', sectionId: 'risks' }
] as const;

const RECOMMENDATION_SUBS: readonly SubStepDef<RecommendationSubId>[] = [
    {
        id: 'consent',
        label: 'Special consent',
        sectionId: 'special-consent',
        presets: SPECIAL_CONSENT_PRESETS
    },
    { id: 'tx-time', label: 'Est. Tx time', sectionId: 'estimated-treatment-time' },
    { id: 'orthopulse', label: 'Orthopulse recommendation', sectionId: 'orthopulse-recommendation' }
] as const;

function DxReportEditor({
    patientId,
    patientName,
    onBack,
    editingEvent,
    resumingDraft,
    startBlank,
    onRegisterBeforeClose
}: DxReportEditorProps) {
    const { showToast } = useToast();
    const isEditMode = !!editingEvent;

    const initialPayload = useMemo<DxReportPayload>(() => {
        // Priority order matters:
        //   1. resumingDraft  - user's saved work-in-progress; always wins.
        //   2. editingEvent   - previous submitted version's payload; skipped
        //      when startBlank is set ("Start over" CTA).
        //   3. Fresh form.
        // editingEvent's groupId still drives buildDraftBody so the save lands
        // in the same group regardless of which branch wins here.
        if (resumingDraft) {
            const extracted = extractPayloadFromEvent(resumingDraft);
            if (extracted) return extracted;
        }
        if (editingEvent && !startBlank) {
            const extracted = extractPayloadFromEvent(editingEvent);
            if (extracted) return extracted;
        }
        return createInitialPayload({ patientName });
    }, [editingEvent, resumingDraft, startBlank, patientName]);

    const [payload, setPayload] = useState<DxReportPayload>(initialPayload);
    const initialPayloadRef = useRef(initialPayload);

    const [stepIndex, setStepIndex] = useState(0);
    const [clinicalSubIndex, setClinicalSubIndex] = useState(0);
    const [recommendationSubIndex, setRecommendationSubIndex] = useState(0);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [practiceId, setPracticeId] = useState<string | null>(null);
    const [accountType, setAccountType] = useState<string | null>(null);
    const [caseType, setCaseType] = useState<string | null>(null);
    const [subCaseType, setSubCaseType] = useState<string | null>(null);
    const [pricedProducts, setPricedProducts] = useState<PricedProduct[]>([]);
    const [productsLoading, setProductsLoading] = useState<boolean>(false);
    const [productsError, setProductsError] = useState<string | null>(null);

    const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);

    const reviewState = payload.review?.state ?? 'editing';

    const latestRequestChangesEntry = useMemo((): DxReviewHistoryEntry | null => {
        const entries = payload.review?.history ?? [];
        const requestEntries = entries.filter((e) => e.action === 'request-changes');
        if (requestEntries.length === 0) return null;
        return [...requestEntries].sort((a, b) => b.at.localeCompare(a.at))[0];
    }, [payload.review?.history]);

    const hasRequestChangesHistory = latestRequestChangesEntry !== null;

    // Legacy drafts keep their request-changes history; show it as a leading Feedback step.
    const STEPS = useMemo(
        () =>
            (hasRequestChangesHistory
                ? [FEEDBACK_STEP, ...BASE_STEPS]
                : BASE_STEPS) as readonly StepDef<StepId>[],
        [hasRequestChangesHistory]
    );

    // Mirrors the same `!startBlank` check used in `initialPayload` above: when
    // the user picks "Start fresh" from a previous version, editingEvent is set
    // but the payload comes from createInitialPayload (not the previous event),
    // so we should treat the editor as brand-new for seeding/image-rewrite
    // purposes — otherwise patient info / first-time section text don't get
    // prefilled like they do on an initial Dx.
    const isResumingOrEditing = (isEditMode && !startBlank) || !!resumingDraft;

    useEffect(() => {
        if (!isResumingOrEditing) return;
        const fileIds = collectImageFileIds(initialPayload);
        if (fileIds.length === 0) return;
        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (cancelled) return;
                setPayload((prev) => rewriteImageSrcs(prev, urls));
            })
            // Non-fatal: images fall back to their stored (possibly stale) src.
            // Swallowed intentionally to avoid surfacing a toast on every resume.
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isResumingOrEditing, patientId, initialPayload]);

    // Always fetch the patient card so we can populate `practiceId`. Payload
    // seeding (patient info + first-time section text) only runs on a brand-
    // new draft so we don't clobber edits the user made on a resumed draft.
    useEffect(() => {
        let cancelled = false;
        fetchPatientCard(patientId)
            .then((card) => {
                if (cancelled) return;
                setPracticeId(card.practice?.id ?? null);
                setAccountType(card.practice?.accountType ?? null);
                setCaseType(card.treatment?.caseType ?? null);
                setSubCaseType(card.treatment?.subCaseType ?? null);

                if (isResumingOrEditing) return;

                const fallback = 'Not specified.';
                const additionalInfo = card.treatment?.additionalInfo?.trim();
                const txDuration = card.treatment?.estimatedTreatmentDuration?.trim();
                const treatmentGoalsText =
                    additionalInfo && additionalInfo.length > 0 ? additionalInfo : fallback;
                const txTimeText = txDuration && txDuration.length > 0 ? txDuration : fallback;

                const dob = toDateOnly(card.dateOfBirth) ?? null;
                const drFirst = card.practice?.drFirstName?.trim() ?? '';
                const drLast = card.practice?.drLastName?.trim() ?? '';
                const drFullName = [drFirst, drLast].filter(Boolean).join(' ');
                const doctorDisplay = drFullName ? `Dr. ${drFullName}` : null;

                setPayload((prev) => {
                    const nextDob = dob ?? prev.patientInfo.dateOfBirth;
                    const next: DxReportPayload = {
                        ...prev,
                        patientInfo: {
                            ...prev.patientInfo,
                            patientName: card.name || prev.patientInfo.patientName,
                            dateOfBirth: nextDob,
                            age: computeAge(nextDob),
                            doctorName: doctorDisplay ?? prev.patientInfo.doctorName,
                            practice: card.practice?.name ?? prev.patientInfo.practice
                        }
                    };
                    const seedSection = (
                        p: DxReportPayload,
                        id: DxReportSectionId,
                        text: string
                    ): DxReportPayload => {
                        const section = p.sections.find((s) => s.id === id);
                        if (!section) return p;
                        const isEmpty =
                            !section.tiptapJson?.content ||
                            section.tiptapJson.content.length === 0 ||
                            (section.tiptapJson.content.length === 1 &&
                                (section.tiptapJson.content[0] as { content?: unknown[] })
                                    .content === undefined);
                        if (!isEmpty) return p;
                        return setSectionTiptap(p, id, tiptapDocFromString(text));
                    };
                    let seeded = next;
                    seeded = seedSection(seeded, 'treatment-goals', treatmentGoalsText);
                    seeded = seedSection(seeded, 'estimated-treatment-time', txTimeText);
                    return seeded;
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [patientId, isResumingOrEditing]);

    // Fetch the product-pricing catalogue (with per-practice prices)
    useEffect(() => {
        if (!practiceId) return;
        let cancelled = false;
        setProductsLoading(true);
        setProductsError(null);
        fetchPracticeProductPricing(practiceId)
            .then((products) => {
                if (cancelled) return;
                setPricedProducts(products);
            })
            .catch(() => {
                if (cancelled) return;
                setProductsError(
                    'Could not load product pricing. You can still enter fees manually.'
                );
            })
            .finally(() => {
                if (cancelled) return;
                setProductsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [practiceId]);

    // OrthoPulse product lookup - passed to setOrthopulseStatus so the
    // recommendation status can keep the OrthoPulse addon in sync.
    const orthopulseProduct = useMemo(
        () => pricedProducts.find((p) => p.name === ORTHOPULSE_PRODUCT_NAME) ?? null,
        [pricedProducts]
    );

    const validity = useMemo(() => validatePayload(payload), [payload]);
    const canPreview = useMemo(() => isPayloadSubmittable(payload), [payload]);

    const isDirty = useMemo(
        () => JSON.stringify(payload) !== JSON.stringify(initialPayloadRef.current),
        [payload]
    );

    const activeClinicalSectionId = CLINICAL_SUBS[clinicalSubIndex].sectionId;
    const activeRecommendationSectionId = RECOMMENDATION_SUBS[recommendationSubIndex].sectionId;
    const stepValid = useCallback(
        (id: StepId): boolean => {
            switch (id) {
                case 'feedback':
                    return true;
                case 'info':
                    return validity['patient-info'].valid;
                case 'fees':
                    return validity['fees'].valid;
                case 'review':
                    return canPreview;
                case 'clinical':
                case 'recommendation': {
                    const sectionIds = STEP_SECTIONS[id];
                    return sectionIds.every((sid) => validity[sid]?.valid);
                }
            }
        },
        [validity, canPreview]
    );

    const nextBlockedReason: string | null = useMemo(() => {
        const cur = STEPS[stepIndex].id;
        switch (cur) {
            case 'feedback':
                return null;
            case 'info':
                return validity['patient-info'].valid
                    ? null
                    : (validity['patient-info'].reason ?? null);
            case 'fees': {
                if (!validity['fees'].valid) return validity['fees'].reason ?? null;
                if (!canPreview) return 'Please complete all required sections before previewing.';
                return null;
            }
            case 'review':
                return null;
            case 'clinical': {
                const isLastSub = clinicalSubIndex >= CLINICAL_SUBS.length - 1;
                if (!isLastSub) {
                    const v = validity[activeClinicalSectionId];
                    return v?.valid ? null : (v?.reason ?? null);
                }
                const missing = STEP_SECTIONS.clinical.find((sid) => !validity[sid]?.valid);
                return missing ? (validity[missing]?.reason ?? null) : null;
            }
            case 'recommendation': {
                const isLastSub = recommendationSubIndex >= RECOMMENDATION_SUBS.length - 1;
                if (!isLastSub) {
                    const v = validity[activeRecommendationSectionId];
                    return v?.valid ? null : (v?.reason ?? null);
                }
                const missing = STEP_SECTIONS.recommendation.find((sid) => !validity[sid]?.valid);
                return missing ? (validity[missing]?.reason ?? null) : null;
            }
        }
    }, [
        STEPS,
        stepIndex,
        clinicalSubIndex,
        recommendationSubIndex,
        validity,
        canPreview,
        activeClinicalSectionId,
        activeRecommendationSectionId
    ]);

    const canAdvance = nextBlockedReason === null;

    const stepComplete = useCallback(
        (id: StepId): boolean => (id === 'review' || id === 'feedback' ? false : stepValid(id)),
        [stepValid]
    );

    const canJumpTo = useCallback(
        (id: StepId): boolean => {
            const targetIndex = STEPS.findIndex((s) => s.id === id);
            if (targetIndex <= stepIndex) return true;
            // Only the review/preview step is gated - all other steps are freely
            // reachable so drafts can be filled in any order.
            if (id === 'review') return canPreview;
            return true;
        },
        [STEPS, stepIndex, canPreview]
    );

    const currentStep = STEPS[stepIndex];

    const updatePatientInfo = useCallback((info: DxReportPatientInfo) => {
        setPayload((p) => ({ ...p, patientInfo: info }));
    }, []);

    const updateFees = useCallback((fees: DxReportFees) => {
        setPayload((p) => ({ ...p, fees }));
    }, []);

    const updateSectionDoc = useCallback((id: DxReportSectionId, doc: TiptapDoc) => {
        setPayload((p) => setSectionTiptap(p, id, doc));
    }, []);

    const goNext = useCallback(() => {
        const cur = STEPS[stepIndex].id;
        if (cur === 'clinical' && clinicalSubIndex < CLINICAL_SUBS.length - 1) {
            setClinicalSubIndex((i) => i + 1);
            return;
        }
        if (cur === 'recommendation' && recommendationSubIndex < RECOMMENDATION_SUBS.length - 1) {
            setRecommendationSubIndex((i) => i + 1);
            return;
        }
        const nextIndex = Math.min(stepIndex + 1, STEPS.length - 1);
        const nextId = STEPS[nextIndex].id;
        if (nextId === 'clinical') setClinicalSubIndex(0);
        if (nextId === 'recommendation') setRecommendationSubIndex(0);
        setStepIndex(nextIndex);
    }, [STEPS, stepIndex, clinicalSubIndex, recommendationSubIndex]);

    const goBack = useCallback(() => {
        const cur = STEPS[stepIndex].id;
        if (cur === 'clinical' && clinicalSubIndex > 0) {
            setClinicalSubIndex((i) => i - 1);
            return;
        }
        if (cur === 'recommendation' && recommendationSubIndex > 0) {
            setRecommendationSubIndex((i) => i - 1);
            return;
        }
        const prevIndex = Math.max(stepIndex - 1, 0);
        const prevId = STEPS[prevIndex].id;
        if (prevId === 'clinical') setClinicalSubIndex(CLINICAL_SUBS.length - 1);
        if (prevId === 'recommendation') setRecommendationSubIndex(RECOMMENDATION_SUBS.length - 1);
        setStepIndex(prevIndex);
    }, [STEPS, stepIndex, clinicalSubIndex, recommendationSubIndex]);

    const handleMainJumpTo = useCallback(
        (id: StepId) => {
            const idx = STEPS.findIndex((s) => s.id === id);
            if (idx < 0) return;
            if (id === 'clinical') setClinicalSubIndex(0);
            if (id === 'recommendation') setRecommendationSubIndex(0);
            setStepIndex(idx);
        },
        [STEPS]
    );

    const buildDraftBody = useCallback(() => {
        const imageFileIds = collectImageFileIds(payload);
        const body: {
            formData: Partial<DxReportPayload>;
            groupId?: string;
            imageFileIds?: string[];
        } = {
            formData: payload,
            imageFileIds: imageFileIds.length > 0 ? imageFileIds : undefined
        };
        if (editingEvent) {
            body.groupId = editingEvent.groupId;
        } else if (resumingDraft?.groupId) {
            body.groupId = resumingDraft.groupId;
        }
        return body;
    }, [payload, editingEvent, resumingDraft]);

    // Draft lifecycle: save handlers, auto-save on step nav, beforeClose
    // registration, and the wrapped handleBack that flushes on outer-close.
    const {
        isSavingDraft,
        isSavingDraftSilent,
        showSavedBadge,
        handleSaveDraft,
        flushDraft,
        handleBack
    } = useDxDraftLifecycle({
        patientId,
        resumingDraft,
        buildDraftBody,
        isDirty,
        stepIndex,
        clinicalSubIndex,
        recommendationSubIndex,
        isOnReviewStep: currentStep.id === 'review',
        onBack,
        onRegisterBeforeClose
    });

    const handleSaveDraftOnly = useCallback(async () => {
        setIsSubmittingForReview(true);
        try {
            const saved = await flushDraft();
            if (saved) {
                showToast('Draft saved', 'success');
                onBack();
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to save draft.';
            showToast(msg, 'error');
        } finally {
            setIsSubmittingForReview(false);
        }
    }, [flushDraft, showToast, onBack]);

    const titleLabel = useMemo(() => {
        if (resumingDraft) {
            return editingEvent ? 'Resume edit draft' : 'Resume draft';
        }
        return isEditMode ? 'Edit Dx report' : 'New Dx report';
    }, [resumingDraft, editingEvent, isEditMode]);

    if (currentStep.id === 'review') {
        return (
            <div className="flex flex-col flex-1 min-h-0">
                <StepHeader
                    steps={STEPS}
                    activeId={currentStep.id}
                    isComplete={stepComplete}
                    canJumpTo={canJumpTo}
                    onJumpTo={handleMainJumpTo}
                />
                <DxReportPreview
                    payload={payload}
                    isSubmitting={isSubmittingForReview}
                    submitError={submitError}
                    submitLabel="Save draft"
                    submitDisabled={!canPreview}
                    onBackToEdit={() => {
                        setSubmitError(null);
                        goBack();
                    }}
                    onSubmit={handleSaveDraftOnly}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={() => {
                        void handleBack();
                    }}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-xs"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back
                </button>
                <div className="flex items-center gap-2 max-w-[60%]">
                    <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                        v{editingEvent ? editingEvent.version + 1 : 1}
                    </span>
                    <div className="text-xs font-semibold text-gray-800 truncate">
                        {titleLabel} · {payload.patientInfo.patientName || 'Untitled'}
                    </div>
                </div>
                <div className="w-[60px] flex items-center justify-end">
                    {isSavingDraftSilent ? (
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                            <span className="w-2.5 h-2.5 rounded-full border border-gray-400 border-t-transparent animate-spin inline-block" />
                            Saving…
                        </span>
                    ) : showSavedBadge ? (
                        <span className="text-[10px] text-emerald-600 font-medium">✓ Saved</span>
                    ) : null}
                </div>
            </div>

            <StepHeader
                steps={STEPS}
                activeId={currentStep.id}
                isComplete={stepComplete}
                canJumpTo={canJumpTo}
                onJumpTo={handleMainJumpTo}
            />

            {currentStep.id === 'feedback' ? (
                <FeedbackStep
                    history={payload.review?.history ?? []}
                    reviewerNote={
                        reviewState === 'editing' ? (payload.review?.reviewerNote ?? null) : null
                    }
                    latestRequestChangesEntry={latestRequestChangesEntry}
                />
            ) : currentStep.id === 'clinical' ? (
                <SubStepSplitView
                    patientId={patientId}
                    payload={payload}
                    subs={CLINICAL_SUBS}
                    activeSubId={CLINICAL_SUBS[clinicalSubIndex].id}
                    onJumpTo={(id) => {
                        const idx = CLINICAL_SUBS.findIndex((s) => s.id === id);
                        if (idx >= 0) setClinicalSubIndex(idx);
                    }}
                    onDocChange={updateSectionDoc}
                    isComplete={(sectionId) => !!validity[sectionId]?.valid}
                />
            ) : currentStep.id === 'recommendation' ? (
                <SubStepSplitView
                    patientId={patientId}
                    payload={payload}
                    subs={RECOMMENDATION_SUBS}
                    activeSubId={RECOMMENDATION_SUBS[recommendationSubIndex].id}
                    onJumpTo={(id) => {
                        const idx = RECOMMENDATION_SUBS.findIndex((s) => s.id === id);
                        if (idx >= 0) setRecommendationSubIndex(idx);
                    }}
                    onDocChange={updateSectionDoc}
                    isComplete={(sectionId) => !!validity[sectionId]?.valid}
                    renderActiveHeader={(subId) =>
                        subId === 'orthopulse' ? (
                            <OrthopulseStatusBar
                                status={payload.orthopulseStatus ?? 'recommended'}
                                onChange={(next) =>
                                    setPayload((p) => {
                                        const withStatus = setOrthopulseStatus(
                                            p,
                                            next,
                                            orthopulseProduct
                                        );
                                        return setSectionTiptap(
                                            withStatus,
                                            'orthopulse-recommendation',
                                            ORTHOPULSE_STATUS_PRESET_DOCS[next]
                                        );
                                    })
                                }
                            />
                        ) : null
                    }
                />
            ) : (
                <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50/30">
                    <div
                        className={
                            'px-6 py-4 flex flex-col gap-4 ' +
                            (currentStep.id === 'fees' ? 'w-full' : 'max-w-3xl mx-auto')
                        }
                    >
                        {currentStep.id === 'info' && (
                            <div className="flex flex-col gap-5">
                                <PatientInfoSection
                                    value={payload.patientInfo}
                                    onChange={updatePatientInfo}
                                    caseType={caseType}
                                    subCaseType={subCaseType}
                                />
                                <PlainTextStep
                                    payload={payload}
                                    sectionIds={INFO_GOAL_SECTIONS}
                                    onDocChange={updateSectionDoc}
                                    labels={{
                                        'treatment-goals': 'Treatment goals'
                                    }}
                                    placeholders={{
                                        'treatment-goals': 'What the treatment aims to achieve.'
                                    }}
                                    rows={3}
                                />
                            </div>
                        )}

                        {currentStep.id === 'fees' && (
                            <FeesSection
                                pricedProducts={pricedProducts}
                                productsLoading={productsLoading}
                                productsError={productsError}
                                value={payload.fees}
                                onChange={updateFees}
                                accountType={accountType}
                            />
                        )}

                        <div className="h-6" />
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 flex-shrink-0">
                <button
                    onClick={goBack}
                    disabled={stepIndex === 0}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>

                <div className="flex items-center gap-3">
                    {nextBlockedReason && (
                        <span className="text-xs text-amber-600">{nextBlockedReason}</span>
                    )}
                    <button
                        onClick={handleSaveDraft}
                        disabled={isSavingDraft}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSavingDraft && (
                            <div className="w-3 h-3 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
                        )}
                        Save draft
                    </button>
                    <button
                        onClick={goNext}
                        disabled={!canAdvance}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {stepIndex === STEPS.length - 2 ? 'Preview' : 'Next'}
                        <ChevronRightIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export { DxReportEditor };
